import { CartAddEvent } from '@theme/events';

/** @type {HTMLDialogElement | null} */
let modal = null;
let scrollY = 0;
let scrollLocked = false;

function getCartSectionIds() {
  /** @type {string[]} */
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.push(el.dataset.sectionId);
    }
  });
  return ids;
}

function getModal() {
  if (!modal) {
    modal = document.querySelector('[data-mcp-extra-modal]');
  }
  return modal;
}

function bindModal() {
  const dialog = getModal();
  if (!(dialog instanceof HTMLDialogElement)) return;

  dialog.querySelector('[data-mcp-extra-modal-close]')?.addEventListener('click', () => {
    closeModal();
  });

  dialog.querySelector('[data-mcp-extra-modal-continue]')?.addEventListener('click', () => {
    closeModal();
  });

  dialog.querySelector('[data-mcp-extra-modal-cart]')?.addEventListener('click', () => {
    closeModal(true);
  });

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) {
      closeModal();
    }
  });

  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeModal();
  });
}

function openModal() {
  const dialog = getModal();
  if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;

  scrollY = window.scrollY;
  document.body.style.width = '100%';
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
  scrollLocked = true;

  dialog.showModal();
}

/**
 * @param {boolean} [goToCart]
 */
function closeModal(goToCart = false) {
  const dialog = getModal();
  if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

  dialog.close();

  if (scrollLocked) {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: scrollY, behavior: 'instant' });
    scrollLocked = false;
  }

  if (goToCart) {
    window.location.assign(Theme.routes.cart_url || '/cart');
  }
}

/**
 * @param {{ title: string, imageSrc?: string, quantity: number }} details
 */
function showAddedModal(details) {
  const dialog = getModal();
  if (!(dialog instanceof HTMLDialogElement)) return;

  const success = dialog.querySelector('[data-mcp-extra-modal-success]');
  const product = dialog.querySelector('[data-mcp-extra-modal-product]');
  const error = dialog.querySelector('[data-mcp-extra-modal-error]');
  const title = dialog.querySelector('[data-mcp-extra-modal-title]');
  const qty = dialog.querySelector('[data-mcp-extra-modal-qty]');
  const image = dialog.querySelector('[data-mcp-extra-modal-image]');
  const cartButton = dialog.querySelector('[data-mcp-extra-modal-cart]');

  success?.classList.remove('hidden');
  product?.classList.remove('hidden');
  error?.classList.add('hidden');

  if (title instanceof HTMLElement) {
    title.textContent = details.title;
  }

  if (qty instanceof HTMLElement) {
    const label = details.quantity > 1 ? 'articles ajoutés' : 'article ajouté';
    qty.textContent = `${details.quantity} ${label}`;
  }

  if (image instanceof HTMLImageElement) {
    if (details.imageSrc) {
      image.src = details.imageSrc;
      image.alt = details.title;
      image.hidden = false;
    } else {
      image.removeAttribute('src');
      image.alt = '';
      image.hidden = true;
    }
  }

  if (cartButton instanceof HTMLButtonElement) {
    cartButton.hidden = false;
  }

  openModal();
}

/**
 * @param {string} message
 */
function showErrorModal(message) {
  const dialog = getModal();
  if (!(dialog instanceof HTMLDialogElement)) return;

  const success = dialog.querySelector('[data-mcp-extra-modal-success]');
  const product = dialog.querySelector('[data-mcp-extra-modal-product]');
  const error = dialog.querySelector('[data-mcp-extra-modal-error]');
  const cartButton = dialog.querySelector('[data-mcp-extra-modal-cart]');

  success?.classList.add('hidden');
  product?.classList.add('hidden');
  error?.classList.remove('hidden');

  if (error instanceof HTMLElement) {
    error.textContent = message;
  }

  if (cartButton instanceof HTMLButtonElement) {
    cartButton.hidden = true;
  }

  openModal();
}

/**
 * @param {HTMLFormElement} form
 */
function getCardDetails(form) {
  const card = form.closest('[data-mcp-extra-card]');
  const titleEl = card?.querySelector('.mcp-pdp-extras__name');
  const imageEl = card?.querySelector('.mcp-pdp-extras__image');
  const qtyInput = form.querySelector('input[name="quantity"]');

  return {
    title: titleEl?.textContent?.trim() || 'Produit',
    imageSrc: imageEl instanceof HTMLImageElement ? imageEl.currentSrc || imageEl.src : '',
    quantity: Math.max(1, parseInt(qtyInput instanceof HTMLInputElement ? qtyInput.value : '1', 10) || 1),
  };
}

function bindSteppers(root) {
  root.querySelectorAll('[data-mcp-extra-stepper]').forEach((stepper) => {
    const input = stepper.querySelector('input[name="quantity"]');
    const decrease = stepper.querySelector('[data-mcp-extra-decrease]');
    const increase = stepper.querySelector('[data-mcp-extra-increase]');
    if (!(input instanceof HTMLInputElement)) return;

    const setQty = (next) => {
      input.value = String(Math.max(1, Math.min(99, next)));
    };

    decrease?.addEventListener('click', (event) => {
      event.preventDefault();
      setQty((parseInt(input.value, 10) || 1) - 1);
    });

    increase?.addEventListener('click', (event) => {
      event.preventDefault();
      setQty((parseInt(input.value, 10) || 1) + 1);
    });

    input.addEventListener('change', () => {
      const parsed = parseInt(input.value, 10);
      setQty(Number.isFinite(parsed) ? parsed : 1);
    });
  });
}

function bindForms(root) {
  root.querySelectorAll('[data-mcp-extra-form]').forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      if (form.hasAttribute('data-mcp-extra-unavailable')) return;

      const submitButton = form.querySelector('[data-mcp-extra-add]');
      if (submitButton instanceof HTMLButtonElement && submitButton.hasAttribute('data-perma-disabled')) return;

      const idInput = form.querySelector('input[name="id"]');
      const qtyInput = form.querySelector('input[name="quantity"]');

      if (!(idInput instanceof HTMLInputElement) || !(qtyInput instanceof HTMLInputElement)) return;

      if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;

      const sections = getCartSectionIds();
      const payload = {
        id: Number(idInput.value),
        quantity: Math.max(1, parseInt(qtyInput.value, 10) || 1),
      };

      if (sections.length) {
        payload.sections = sections.join(',');
        payload.sections_url = window.location.pathname;
      }

      try {
        const response = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json();

        if (data.status) {
          showErrorModal(data.description || data.message || 'Impossible d’ajouter au panier.');
          if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
          return;
        }

        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        document.dispatchEvent(
          new CartAddEvent(cart, form.id || 'mcp-pdp-extras', {
            source: 'mcp-pdp-extras',
            itemCount: cart.item_count,
            sections: data.sections,
          })
        );

        showAddedModal(getCardDetails(form));

        if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
      } catch (error) {
        console.error(error);
        showErrorModal('Une erreur est survenue.');
        if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
      }
    });
  });
}

function initExtras() {
  bindModal();

  document.querySelectorAll('[data-mcp-pdp-extras]').forEach((root) => {
    bindSteppers(root);
    bindForms(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initExtras);
} else {
  initExtras();
}
